/**
 * Page Layout Component
 * 
 * This component provides a consistent grid layout structure for the entire application.
 * It uses Material UI's Grid system to create a responsive layout that works across all device sizes.
 */

import React from 'react';
import { Container, Box } from '@mui/material';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';

// Styled container with max width and padding
const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  }
}));

// Main content area with proper spacing
const MainContent = styled(Box)(({ theme }) => ({
  minHeight: 'calc(100vh - 180px)', // Account for header and footer
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(4),
}));

/**
 * PageLayout component that provides a consistent grid structure
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.header - Header component
 * @param {React.ReactNode} props.footer - Footer component
 * @param {React.ReactNode} props.children - Main content
 * @param {boolean} props.fullWidth - Whether to use full width container
 * @returns {React.ReactElement} Rendered component
 */
const PageLayout = ({ header, footer, children, fullWidth = false }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      {header}
      
      {/* Main Content */}
      <StyledContainer maxWidth={fullWidth ? false : 'lg'} component="main">
        <MainContent>
          {children}
        </MainContent>
      </StyledContainer>
      
      {/* Footer */}
      {footer}
    </Box>
  );
};

PageLayout.propTypes = {
  header: PropTypes.node.isRequired,
  footer: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  fullWidth: PropTypes.bool
};

export default PageLayout;
